import React from 'react';
import {Button, Col, Label, Row} from 'reactstrap';
import {AvForm, AvGroup} from 'availity-reactstrap-validation';
import Translate from '../../shared/language/Translate';
import GeneralUtils from '../../shared/utils/GeneralUtils';
import AvField from '../../shared/component/form/AvField';
import FormSection from '../../shared/component/form/FormSection';
import PurchaseRequisitionService from '../../shared/service/PurchaseRequisitionService';
import SupplierService from '../../shared/service/SupplierService';
import PurchaseRequisitionItemColumn from '../../shared/component/dataTable/columns/PurchaseRequisitionItemColumn.json';
import PurchaseRequisitionAssignPoColumn from '../../shared/component/dataTable/columns/PurchaseRequisitionAssignPoColumn.json';
import PurchaseRequisitionDeliveryCompareColumn from '../../shared/component/dataTable/columns/PurchaseRequisitionDeliveryCompareColumn.json';
import Dialog from '../../shared/component/dialog/Dialog';
import Loading from '../../shared/component/others/Loading';
import ModalConfirm from '../../shared/component/dialog/ModalConfirm';
import PurchaseRequisitionItemMobileSave from '../purchaserequisitionmobile/PurchaseRequisitionItemMobileSave';
import PurchaseRequisitionItemDataTable from './PurchaseRequisitionItemDataTable';
import DataTableAll from '../../shared/component/dataTable/DataTableAll';
import {applyCostWarningsToItems, buildCheckoutPayload, buildDeliveryPoComparisonRows, countDeliveryPoComparisonIssues, exportSupplierPdf, formatCheckoutValidationErrors, getLoggedInUserEmail, getSupplierMailDetails, groupItemsBySupplier, normalizeRequisition, persistRequisitionToDb, removeItemFromList, upsertRequisitionItem, validateItemsForCheckout, validateCheckoutEmailOptions, buildInitialCheckoutOptions, wasSupplierEmailSent} from './PurchaseRequisitionUtils';
import {Checkbox} from 'primereact/checkbox';
import dayjs from 'dayjs';
import {toast} from 'react-toastify';
import {PURCHASE_REQUISITION_STATUS} from '../../shared/utils/Constants';
import '../../shared/styles/SettingsPage.css';

const defaultRequisition = {
    id: null,
    description: '',
    requisitionStatus: 'OPEN',
    approved: false,
    approvedDate: null,
    createDate: null,
    itemList: []
};

const defaultItem = {
    id: null,
    barcode: null,
    itemName: null,
    itemBssId: null,
    itemUnitMeasure: null,
    itemSize: null,
    boxSize: null,
    deliveredBoxCount: null,
    supplierId: null,
    supplierName: null,
    supplierBssId: null,
    currentCost: null,
    minStock: null,
    maxStock: null,
    recommendedOrderAmount: null,
    actualOrderAmount: null,
    damagedQuantity: null,
    expiryDate: null,
    currentStock: null,
    status: 'ACTIVE'
};

export default class PurchaseRequisitionSave extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            requisition: Object.assign({}, defaultRequisition),
            loading: false,
            displayDialog: false,
            displayDeleteConfirmDialog: false,
            displayRemoveItemConfirmDialog: false,
            itemToRemove: null,
            displayCheckoutDialog: false,
            checkoutOptions: {},
            displayCheckoutConfirmDialog: false,
            displayApproveConfirmDialog: false,
            displayAssignPurchaseOrderDialog: false,
            availablePurchaseOrders: [],
            selectedPurchaseOrderId: null,
            assignedPurchaseOrder: null,
            comparisonRows: [],
            comparisonVersion: 0,
            loggedInUserEmail: getLoggedInUserEmail()
        };
        this.purchaseRequisitionService = new PurchaseRequisitionService();
        this.supplierService = new SupplierService();
    }

    componentDidMount() {
        if (GeneralUtils.isMobile()) {
            window.location.href = '#purchaseRequisitionMobile';
            return;
        }
        const id = this.getRequisitionIdFromUrl();
        if (!GeneralUtils.__isValueEmpty(id) && id !== '-1') {
            this.loadRequisition(id);
        } else {
            this.initNewRequisition();
        }
    }

    getRequisitionIdFromUrl = () => {
        const href = window.location.href;
        const query = href.split('?')[1];
        if (GeneralUtils.__isValueEmpty(query)) {
            return null;
        }
        const idPart = query.split('&')[0];
        if (!GeneralUtils.__isValueEmpty(idPart) && idPart !== '-1') {
            return idPart;
        }
        return null;
    };

    isDeliveryModeFromUrl = () => {
        const href = window.location.href;
        const query = href.split('?')[1] || '';
        return query.includes('mode=delivery');
    };

    isDeliveryNote = () => this.state.requisition?.requisitionStatus === 'DELIVERY_NOTE';

    isApproved = () => {
        const approved = this.state.requisition?.approved;
        return approved === true || approved === 'true' || approved === 1 || approved === '1';
    };

    initNewRequisition = () => {
        const isDelivery = this.isDeliveryModeFromUrl();
        this.setState({
            requisition: {
                ...defaultRequisition,
                requisitionStatus: isDelivery ? 'DELIVERY_NOTE' : 'OPEN',
                createDate: dayjs().format('DD/MM/YYYY'),
                itemList: []
            }
        });
    };

    loadRequisition = (id) => {
        this.setState({loading: true});
        this.purchaseRequisitionService.findById(id).then(response => {
            if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)) {
                this.applyCostWarnings(normalizeRequisition(response)).then(requisition => {
                    this.setState({requisition, loading: false}, () => {
                        this.loadAssignedPurchaseOrder(requisition.assignedPurchaseRequisitionId);
                    });
                });
            } else {
                this.setState({loading: false});
            }
        }).catch(() => this.setState({loading: false}));
    };

    loadAssignedPurchaseOrder = (purchaseOrderId) => {
        if (GeneralUtils.isNullOrEmpty(purchaseOrderId)) {
            this.setState(prev => ({
                assignedPurchaseOrder: null,
                comparisonRows: [],
                comparisonVersion: (prev.comparisonVersion || 0) + 1
            }));
            return Promise.resolve(null);
        }
        return this.purchaseRequisitionService.findById(purchaseOrderId).then(response => {
            if (GeneralUtils.isNullOrEmpty(response) || !GeneralUtils.isNullOrEmpty(response.type)) {
                this.setState(prev => ({
                    assignedPurchaseOrder: null,
                    comparisonRows: [],
                    comparisonVersion: (prev.comparisonVersion || 0) + 1
                }));
                return null;
            }
            const assignedPurchaseOrder = normalizeRequisition(response);
            const dnItems = this.state.requisition.itemList || [];
            const comparisonRows = buildDeliveryPoComparisonRows(
                assignedPurchaseOrder.itemList,
                dnItems
            );
            this.setState(prev => ({
                assignedPurchaseOrder,
                comparisonRows,
                comparisonVersion: (prev.comparisonVersion || 0) + 1
            }));
            return assignedPurchaseOrder;
        }).catch(() => {
            this.setState(prev => ({
                assignedPurchaseOrder: null,
                comparisonRows: [],
                comparisonVersion: (prev.comparisonVersion || 0) + 1
            }));
            return null;
        });
    };

    refreshComparisonRows = () => {
        const assignedPurchaseOrder = this.state.assignedPurchaseOrder;
        if (!assignedPurchaseOrder) {
            this.setState({comparisonRows: []});
            return [];
        }
        const comparisonRows = buildDeliveryPoComparisonRows(
            assignedPurchaseOrder.itemList,
            this.state.requisition.itemList
        );
        this.setState({comparisonRows});
        return comparisonRows;
    };

    findDeliveryNoteItemByBarcode = (barcode) => {
        const key = String(barcode || '').trim();
        if (!key) {
            return null;
        }
        return (this.state.requisition.itemList || []).find(item => String(item?.barcode || '').trim() === key) || null;
    };

    getDeliveryTableData = () => {
        if (!this.state.assignedPurchaseOrder) {
            return this.state.requisition.itemList || [];
        }
        return (this.state.comparisonRows || []).map(row => {
            const dnItem = this.findDeliveryNoteItemByBarcode(row.barcode);
            return {
                ...row,
                hideButton: !dnItem || !this.isEditable(),
                _dnItem: dnItem
            };
        });
    };

    applyCostWarnings = (requisition) => {
        if (!requisition?.id || requisition.requisitionStatus !== 'OPEN') {
            return Promise.resolve(requisition);
        }
        return this.purchaseRequisitionService.getCostWarnings(requisition.id).then(warnings => {
            if (GeneralUtils.isNullOrEmpty(warnings) || !GeneralUtils.isNullOrEmpty(warnings.type)) {
                return requisition;
            }
            return {
                ...requisition,
                itemList: applyCostWarningsToItems(requisition.itemList, warnings)
            };
        }).catch(() => requisition);
    };

    refreshRequisitionWithCostWarnings = (requisition) => {
        return this.applyCostWarnings(requisition).then(enriched => {
            this.setState({requisition: enriched});
            return enriched;
        });
    };

    isEditable = () => {
        if (this.isApproved()) {
            return false;
        }
        const status = this.state.requisition?.requisitionStatus;
        return status === 'OPEN' || status === 'DELIVERY_NOTE';
    };

    getRequisitionStatusLabel = (status) => {
        const match = PURCHASE_REQUISITION_STATUS.find(item => item.value === status);
        let label = match?.name || status || 'Requisition';
        if (status === 'DELIVERY_NOTE' && this.isApproved()) {
            label = `${label} (Closed)`;
        }
        return label;
    };

    hasSavedRequisition = () => !GeneralUtils.isNullOrEmpty(this.state.requisition?.id);

    showDeleteConfirmDialog = () => {
        if (!this.hasSavedRequisition() || this.isApproved()) {
            return;
        }
        this.setState({displayDeleteConfirmDialog: true});
    };

    deleteRequisition = () => {
        const id = this.state.requisition?.id;
        if (GeneralUtils.isNullOrEmpty(id)) {
            this.setState({displayDeleteConfirmDialog: false});
            return;
        }

        this.setState({loading: true, displayDeleteConfirmDialog: false});
        this.purchaseRequisitionService.delete(id).then(response => {
            if (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type)) {
                window.location.href = '#purchaseRequisition';
            } else {
                this.setState({loading: false});
            }
        }).catch(() => this.setState({loading: false}));
    };

    saveRequisition = () => {
        if (!this.isEditable()) {
            return;
        }
        const requisition = {...this.state.requisition};
        const itemList = Array.isArray(requisition.itemList) ? requisition.itemList : [];

        if (itemList.length === 0) {
            toast.warn('Please add at least one item!');
            return;
        }

        this.setState({loading: true});
        persistRequisitionToDb(requisition, this.purchaseRequisitionService).then(result => {
            if (!result.success) {
                this.setState({loading: false});
                return;
            }
            const saved = result.requisition;
            const hadId = !GeneralUtils.isNullOrEmpty(requisition.id);
            this.applyCostWarnings(saved).then(enriched => {
                this.setState({requisition: enriched, loading: false}, () => {
                    if (!hadId && enriched.id) {
                        window.location.href = '#purchaseRequisitionSave?' + enriched.id;
                    }
                });
            });
        });
    };

    onDataSelect = (data) => {
        let itemData = data;
        // Comparison rows (PO assigned): only open real DN items, not PO-only / unexpected rows
        if (this.isDeliveryNote() && this.state.assignedPurchaseOrder) {
            if (data?.matchStatus === 'MISSING') {
                toast.warn('Ordered on Purchase Order but not received on Delivery Note yet');
                return;
            }
            const dnItem = data?._dnItem || this.findDeliveryNoteItemByBarcode(data?.barcode);
            if (!dnItem) {
                toast.warn('Item is not on this Delivery Note');
                return;
            }
            itemData = dnItem;
        }
        GeneralUtils.openDialogThenRef(this, {displayDialog: true}, () => this.refModelSave, (ref) => ref.setData(itemData));
    };

    addNewItem = () => {
        if (!this.isEditable()) {
            return;
        }
        const item = Object.assign({}, defaultItem);
        if (this.isDeliveryNote()) {
            item.deliveredBoxCount = 1;
        }
        GeneralUtils.openDialogThenRef(
            this,
            {displayDialog: true},
            () => this.refModelSave,
            (ref) => ref.setData(item)
        );
    };

    persistItemAndContinue = (itemData, closeAfterSave = true) => {
        if (!this.isEditable()) {
            return;
        }
        const {itemList} = upsertRequisitionItem(this.state.requisition.itemList, itemData);
        const requisition = {...this.state.requisition, itemList};
        const hadRequisitionId = !GeneralUtils.isNullOrEmpty(requisition.id);

        this.setState({loading: true});
        persistRequisitionToDb(requisition, this.purchaseRequisitionService).then(result => {
            if (!result.success) {
                this.setState({loading: false});
                return;
            }
            this.applyCostWarnings(result.requisition).then(enriched => {
                this.setState({requisition: enriched, loading: false}, () => {
                    this.refreshComparisonRows();
                    if (!hadRequisitionId && enriched.id) {
                        window.history.replaceState(null, '', '#purchaseRequisitionSave?' + enriched.id);
                    }
                    if (closeAfterSave) {
                        this.closeItemDialog();
                        return;
                    }
                    const nextItem = Object.assign({}, defaultItem);
                    if (this.isDeliveryNote()) {
                        nextItem.deliveredBoxCount = 1;
                    }
                    GeneralUtils.openDialogThenRef(
                        this,
                        {displayDialog: true},
                        () => this.refModelSave,
                        (ref) => ref.setData(nextItem)
                    );
                });
            });
        });
    };

    saveItem = (itemData) => this.persistItemAndContinue(itemData, true);

    saveItemAndNext = (itemData) => this.persistItemAndContinue(itemData, false);

    closeItemDialog = () => {
        this.setState({displayDialog: false});
    };

    __handleChange = (e) => {
        if (!this.isEditable()) {
            return;
        }
        const value = e.target.parsedValue !== undefined ? e.target.parsedValue : e.target.value;
        this.setState(prev => ({
            requisition: {...prev.requisition, [e.target.name]: value}
        }));
    };

    getItemListFields = () => {
        const isDelivery = this.isDeliveryNote();
        const hasAssignedPo = isDelivery && !!this.state.assignedPurchaseOrder;
        if (hasAssignedPo) {
            return PurchaseRequisitionDeliveryCompareColumn.fields
                .filter(field => {
                    if (field.field === 'button.delete' && !this.isEditable()) {
                        return false;
                    }
                    return field.visible !== false;
                })
                .map(field => ({...field, visible: true}));
        }
        const deliveryFields = new Set([
            'barcode',
            'itemNameWithSizeAndQuantity',
            'boxSize',
            'deliveredBoxCount',
            'actualOrderAmount',
            'damagedQuantity',
            'expiryDate',
            'button.delete'
        ]);
        return PurchaseRequisitionItemColumn.fields
            .filter(field => {
                if (field.field === 'button.delete' && !this.isEditable()) {
                    return false;
                }
                if (isDelivery) {
                    return deliveryFields.has(field.field);
                }
                if (field.field === 'damagedQuantity' || field.field === 'expiryDate' || field.field === 'deliveredBoxCount') {
                    return false;
                }
                return field.visible !== false;
            })
            .map(field => {
                if (!isDelivery) {
                    return field;
                }
                if (field.field === 'itemNameWithSizeAndQuantity') {
                    return {...field, field: 'itemName', header: 'Name', visible: true};
                }
                if (field.field === 'boxSize') {
                    return {...field, header: 'Units per Box', visible: true};
                }
                if (field.field === 'deliveredBoxCount') {
                    return {...field, header: 'Number of Boxes', visible: true};
                }
                if (field.field === 'damagedQuantity') {
                    return {...field, header: 'Waste Quantity', visible: true};
                }
                if (field.field === 'actualOrderAmount') {
                    return {...field, header: 'Total Quantity', visible: true};
                }
                if (field.field === 'expiryDate') {
                    return {...field, visible: true};
                }
                return {...field, visible: true};
            });
    };

    renderItemList() {
        if (this.isDeliveryNote()) {
            const hasAssignedPo = !!this.state.assignedPurchaseOrder;
            const items = hasAssignedPo ? this.getDeliveryTableData() : (this.state.requisition.itemList || []);
            const issueCount = countDeliveryPoComparisonIssues(this.state.comparisonRows);
            if (!hasAssignedPo && items.length === 0) {
                return (
                    <div className="text-muted" style={{padding: '12px 0'}}>
                        No items added yet.
                    </div>
                );
            }
            return (
                <div>
                    {hasAssignedPo &&
                        <div className={`purchase-requisition-compare-dialog__summary${issueCount > 0 ? ' has-issues' : ''}`}>
                            {issueCount > 0
                                ? `${issueCount} difference(s) between ordered and received items.`
                                : 'Ordered and received quantities match.'}
                        </div>
                    }
                    <PurchaseRequisitionItemDataTable
                        key={`dn-items-${hasAssignedPo ? (this.state.assignedPurchaseOrder?.id || 'po') : 'plain'}-${this.state.comparisonVersion || 0}`}
                        data={items}
                        selectionMode="single"
                        paginator={true}
                        rows={20}
                        dataKey={hasAssignedPo ? 'barcode' : 'id'}
                        onDataSelect={this.onDataSelect}
                        delete={this.isEditable() ? this.showRemoveItemConfirm : undefined}
                        fields={this.getItemListFields()}
                    />
                </div>
            );
        }
        return this.renderSupplierGroups();
    }

    showRemoveItemConfirm = (rowData) => {
        if (!this.isEditable()) {
            return;
        }
        const itemToRemove = rowData?._dnItem || (this.state.assignedPurchaseOrder
            ? this.findDeliveryNoteItemByBarcode(rowData?.barcode)
            : rowData);
        if (!itemToRemove) {
            toast.warn('Item is not on this Delivery Note');
            return;
        }
        this.setState({
            displayRemoveItemConfirmDialog: true,
            itemToRemove
        });
    };

    confirmRemoveItem = () => {
        const itemToRemove = this.state.itemToRemove;
        if (!itemToRemove) {
            this.setState({displayRemoveItemConfirmDialog: false});
            return;
        }

        const itemList = removeItemFromList(this.state.requisition.itemList, itemToRemove);
        if (itemList.length === 0) {
            toast.warn('Requisition must have at least one item!');
            this.setState({displayRemoveItemConfirmDialog: false, itemToRemove: null});
            return;
        }

        const requisition = {...this.state.requisition, itemList};
        if (!this.hasSavedRequisition()) {
            this.setState({
                requisition,
                displayRemoveItemConfirmDialog: false,
                itemToRemove: null
            });
            return;
        }

        this.setState({loading: true, displayRemoveItemConfirmDialog: false});
        persistRequisitionToDb(requisition, this.purchaseRequisitionService).then(result => {
            if (!result.success) {
                this.setState({loading: false, itemToRemove: null});
                return;
            }
            this.applyCostWarnings(result.requisition).then(enriched => {
                this.setState({requisition: enriched, loading: false, itemToRemove: null}, () => {
                    this.refreshComparisonRows();
                });
            });
        });
    };

    openAssignPurchaseOrderDialog = () => {
        if (!this.isDeliveryNote() || !this.hasSavedRequisition()) {
            toast.warn('Please save the delivery note first');
            return;
        }
        this.setState({loading: true});
        this.purchaseRequisitionService.findAvailablePurchaseOrders(this.state.requisition.id).then(response => {
            if (GeneralUtils.isNullOrEmpty(response) || !GeneralUtils.isNullOrEmpty(response.type)) {
                this.setState({loading: false});
                return;
            }
            const options = Array.isArray(response) ? response : [];
            const currentId = this.state.requisition.assignedPurchaseRequisitionId;
            const currentNo = this.state.requisition.assignedPurchaseRequisitionNo;
            if (currentId && !options.some(option => Number(option.id) === Number(currentId))) {
                options.unshift({
                    id: currentId,
                    requisitionNo: currentNo || String(currentId),
                    description: 'Currently assigned'
                });
            }
            this.setState({
                availablePurchaseOrders: options,
                selectedPurchaseOrderId: currentId || '',
                displayAssignPurchaseOrderDialog: true,
                loading: false
            });
        }).catch(() => this.setState({loading: false}));
    };

    closeAssignPurchaseOrderDialog = () => {
        this.setState({
            displayAssignPurchaseOrderDialog: false,
            availablePurchaseOrders: [],
            selectedPurchaseOrderId: null
        });
    };

    confirmAssignPurchaseOrder = () => {
        const deliveryNoteId = this.state.requisition.id;
        const selectedId = this.state.selectedPurchaseOrderId;
        const assignedPurchaseRequisitionId = GeneralUtils.isNullOrEmpty(selectedId) ? null : Number(selectedId);
        this.setState({
            loading: true,
            displayAssignPurchaseOrderDialog: false,
            assignedPurchaseOrder: null,
            comparisonRows: []
        });
        this.purchaseRequisitionService.assignPurchaseOrder(deliveryNoteId, assignedPurchaseRequisitionId).then(response => {
            if (GeneralUtils.isNullOrEmpty(response) || !GeneralUtils.isNullOrEmpty(response.type)) {
                this.setState({loading: false});
                return;
            }
            const saved = normalizeRequisition(response);
            this.setState(prev => ({
                requisition: {
                    ...prev.requisition,
                    assignedPurchaseRequisitionId: saved.assignedPurchaseRequisitionId ?? null,
                    assignedPurchaseRequisitionNo: saved.assignedPurchaseRequisitionNo || null,
                    // keep current DN items; assign response is only for linkage fields
                    itemList: prev.requisition.itemList || []
                },
                availablePurchaseOrders: [],
                selectedPurchaseOrderId: null,
                assignedPurchaseOrder: null,
                comparisonRows: [],
                comparisonVersion: (prev.comparisonVersion || 0) + 1
            }), () => {
                this.loadAssignedPurchaseOrder(assignedPurchaseRequisitionId).then(() => {
                    this.setState({loading: false});
                });
            });
        }).catch(() => this.setState({loading: false}));
    };

    onAssignPurchaseOrderSelect = (data) => {
        if (!data?.id) {
            return;
        }
        this.setState({selectedPurchaseOrderId: data.id}, () => this.confirmAssignPurchaseOrder());
    };

    clearAssignedPurchaseOrder = () => {
        this.setState({selectedPurchaseOrderId: ''}, () => this.confirmAssignPurchaseOrder());
    };

    renderAssignPurchaseOrderDialog() {
        return (
            <Dialog
                visible={this.state.displayAssignPurchaseOrderDialog}
                header="Purchase Order"
                onHide={this.closeAssignPurchaseOrderDialog}
                style={{width: '720px', maxWidth: '95vw'}}
            >
                <div className="settings-page settings-page--dialog settings-page--lookup-picker purchase-requisition-assign-po-dialog">
                    <div className="purchase-requisition-po-card purchase-requisition-po-card--dialog">
                        <div className="purchase-requisition-po-card__title">Purchase Order</div>
                        <div className="purchase-requisition-po-card__meta">
                            Same branch · not already assigned to another Delivery Note
                        </div>
                    </div>
                    <div className="settings-page__table-wrap settings-page__table-wrap--lookup-dialog">
                        <DataTableAll
                            fields={PurchaseRequisitionAssignPoColumn.fields}
                            objectName={PurchaseRequisitionAssignPoColumn.name}
                            data={this.state.availablePurchaseOrders || []}
                            rows={10}
                            selectionMode="single"
                            onDataSelect={this.onAssignPurchaseOrderSelect}
                        />
                    </div>
                    <div className="settings-page__actions settings-page__actions--dialog-footer purchase-requisition-assign-po-dialog__footer">
                        {this.state.requisition.assignedPurchaseRequisitionId &&
                            <Button
                                color="outline-primary"
                                className="settings-page__btn--cancel"
                                onClick={this.clearAssignedPurchaseOrder}
                            >
                                Clear
                            </Button>
                        }
                        <Button
                            color="outline-primary"
                            className="settings-page__btn--cancel settings-page__btn--close"
                            onClick={this.closeAssignPurchaseOrderDialog}
                        >
                            Close
                        </Button>
                    </div>
                </div>
            </Dialog>
        );
    }

    renderAssignedPurchaseOrderCard() {
        if (!this.isDeliveryNote()) {
            return null;
        }
        const assigned = this.state.assignedPurchaseOrder;
        const assignedNo = this.state.requisition.assignedPurchaseRequisitionNo;
        const issueCount = countDeliveryPoComparisonIssues(this.state.comparisonRows);
        const editable = this.isEditable();

        return (
            <div className="purchase-requisition-po-card">
                <div className="purchase-requisition-po-card__header">
                    <div>
                        <div className="purchase-requisition-po-card__eyebrow">Purchase Order</div>
                        <div className="purchase-requisition-po-card__title">
                            {assignedNo || assigned?.requisitionNo || 'Not assigned'}
                        </div>
                    </div>
                    <div className="purchase-requisition-po-card__actions">
                        {editable &&
                            <i
                                className={`pi pi-search purchase-requisition-po-card__search${this.hasSavedRequisition() ? '' : ' is-disabled'}`}
                                role="button"
                                tabIndex={this.hasSavedRequisition() ? 0 : -1}
                                title={this.hasSavedRequisition() ? 'Search Purchase Order' : 'Save delivery note first'}
                                onClick={() => {
                                    if (!this.hasSavedRequisition()) {
                                        toast.warn('Please save the delivery note first');
                                        return;
                                    }
                                    this.openAssignPurchaseOrderDialog();
                                }}
                            />
                        }
                    </div>
                </div>
                {assigned &&
                    <div className="purchase-requisition-po-card__body">
                        <div className="purchase-requisition-po-card__field">
                            <span className="purchase-requisition-po-card__label">Checkout Date</span>
                            <span className="purchase-requisition-po-card__value">{assigned.checkoutDate || '—'}</span>
                        </div>
                        <div className="purchase-requisition-po-card__field">
                            <span className="purchase-requisition-po-card__label">Create Date</span>
                            <span className="purchase-requisition-po-card__value">{assigned.createDate || '—'}</span>
                        </div>
                    </div>
                }
                {assignedNo && issueCount > 0 &&
                    <div className="purchase-requisition-po-card__footer">
                        <span className="purchase-requisition-po-card__warning">
                            {issueCount} quantity / item mismatch(es) — see table below
                        </span>
                    </div>
                }
            </div>
        );
    }

    openCheckoutDialog = () => {
        if (this.isDeliveryNote() || !this.isEditable() || !this.hasSavedRequisition()) {
            return;
        }
        if (!this.validateCheckoutItems()) {
            return;
        }
        const itemList = this.state.requisition.itemList || [];
        const groups = groupItemsBySupplier(itemList);
        const checkoutOptions = buildInitialCheckoutOptions(itemList);
        this.setState({
            displayCheckoutDialog: true,
            checkoutOptions,
            loggedInUserEmail: getLoggedInUserEmail()
        }, () => this.loadSupplierEmailsForCheckout(groups, checkoutOptions));
    };

    openApproveDialog = () => {
        if (!this.isDeliveryNote() || !this.isEditable() || !this.hasSavedRequisition()) {
            return;
        }
        const itemList = this.state.requisition.itemList || [];
        if (itemList.length === 0) {
            toast.warn('Please add at least one item!');
            return;
        }
        this.setState({displayApproveConfirmDialog: true});
    };

    executeApprove = () => {
        const id = this.state.requisition?.id;
        if (GeneralUtils.isNullOrEmpty(id)) {
            this.setState({displayApproveConfirmDialog: false});
            return;
        }
        this.setState({loading: true, displayApproveConfirmDialog: false});
        this.purchaseRequisitionService.approve(id).then(response => {
            if (GeneralUtils.isNullOrEmpty(response) || !GeneralUtils.isNullOrEmpty(response.type)) {
                // Already approved / error — reload so Approve/Save/Delete hide
                this.loadRequisition(id);
                return;
            }
            const saved = normalizeRequisition({
                ...response,
                approved: true
            });
            this.applyCostWarnings(saved).then(enriched => {
                this.setState({
                    requisition: {...enriched, approved: true},
                    loading: false
                }, () => this.refreshComparisonRows());
            });
        }).catch(() => this.loadRequisition(id));
    };

    loadSupplierEmailsForCheckout = (groups, checkoutOptions) => {
        const requests = groups
            .filter(group => group.supplierId)
            .map(group => this.supplierService.findById(group.supplierId).then(response => ({
                key: group.key,
                supplierEmail: (!GeneralUtils.isNullOrEmpty(response) && GeneralUtils.isNullOrEmpty(response.type))
                    ? (response.email || '')
                    : ''
            })).catch(() => ({key: group.key, supplierEmail: ''})));

        if (requests.length === 0) {
            return;
        }

        Promise.all(requests).then(results => {
            const merged = {...checkoutOptions};
            results.forEach(result => {
                if (!result?.key || !merged[result.key]) {
                    return;
                }
                merged[result.key] = {
                    ...merged[result.key],
                    supplierEmail: result.supplierEmail || ''
                };
            });
            this.setState({checkoutOptions: merged});
        });
    };

    closeCheckoutDialog = () => {
        this.setState({displayCheckoutDialog: false, checkoutOptions: {}});
    };

    toggleCheckoutOption = (groupKey, field, checked) => {
        this.setState(prev => {
            const current = prev.checkoutOptions[groupKey] || {};
            const updated = {
                ...current,
                [field]: checked
            };
            if (field === 'email' && checked && GeneralUtils.isNullOrEmpty(updated.ccEmail)) {
                updated.ccEmail = getLoggedInUserEmail();
            }
            return {
                checkoutOptions: {
                    ...prev.checkoutOptions,
                    [groupKey]: updated
                }
            };
        });
    };

    updateCheckoutSupplierEmail = (groupKey, email) => {
        this.setState(prev => ({
            checkoutOptions: {
                ...prev.checkoutOptions,
                [groupKey]: {
                    ...prev.checkoutOptions[groupKey],
                    supplierEmail: email
                }
            }
        }));
    };

    updateCheckoutCcEmail = (groupKey, email) => {
        this.setState(prev => ({
            checkoutOptions: {
                ...prev.checkoutOptions,
                [groupKey]: {
                    ...prev.checkoutOptions[groupKey],
                    ccEmail: email
                }
            }
        }));
    };

    processCheckoutOutputs = async (groups, requisition, checkoutOptions) => {
        for (const group of groups) {
            const options = checkoutOptions[group.key];
            if (!options?.print) {
                continue;
            }
            exportSupplierPdf(group, requisition);
        }
    };

    validateCheckoutItems = () => {
        const itemList = this.state.requisition.itemList || [];
        if (itemList.length === 0) {
            toast.warn('Please add at least one item!');
            return false;
        }
        const validationErrors = validateItemsForCheckout(itemList);
        const message = formatCheckoutValidationErrors(validationErrors);
        if (message) {
            toast.warn(message);
            return false;
        }
        return true;
    };

    confirmCheckout = () => {
        if (!this.isEditable() || !this.hasSavedRequisition()) {
            return;
        }

        if (!this.validateCheckoutItems()) {
            return;
        }

        const emailValidationErrors = validateCheckoutEmailOptions(this.state.checkoutOptions);
        if (emailValidationErrors.length > 0) {
            toast.warn(emailValidationErrors[0]);
            return;
        }

        this.setState({displayCheckoutConfirmDialog: true});
    };

    executeCheckout = () => {
        if (!this.validateCheckoutItems()) {
            this.setState({displayCheckoutConfirmDialog: false});
            return;
        }

        const requisition = {...this.state.requisition};
        const itemList = requisition.itemList || [];
        const groups = groupItemsBySupplier(itemList);
        const checkoutOptions = {...this.state.checkoutOptions};
        const payload = buildCheckoutPayload(requisition, checkoutOptions);

        this.setState({loading: true, displayCheckoutConfirmDialog: false});
        this.purchaseRequisitionService.checkout(payload.id, payload).then(response => {
            if (GeneralUtils.isNullOrEmpty(response) || !GeneralUtils.isNullOrEmpty(response.type)) {
                this.setState({loading: false});
                return;
            }
            const saved = normalizeRequisition(response);
            this.setState({
                requisition: saved,
                loading: false,
                displayCheckoutDialog: false,
                checkoutOptions: {}
            }, async () => {
                await this.processCheckoutOutputs(groups, saved, checkoutOptions);
            });
        }).catch(() => this.setState({loading: false}));
    };

    renderCheckoutDialog() {
        const groups = groupItemsBySupplier(this.state.requisition.itemList);
        const hasEmailSelected = Object.values(this.state.checkoutOptions || {}).some(option => option.email === true);
        const isNonProduction = !GeneralUtils.isProductionEnvironment();
        return (
            <Dialog
                visible={this.state.displayCheckoutDialog}
                header="Checkout"
                onHide={this.closeCheckoutDialog}
                style={{width: '620px', maxWidth: '95vw'}}
            >
                <div className="purchase-requisition-checkout-dialog">
                    <p className="purchase-requisition-checkout-dialog__intro">
                        Select print and email options for each supplier before completing checkout.
                    </p>
                    {hasEmailSelected && isNonProduction &&
                        <div className="purchase-requisition-checkout-dialog__env-note" role="status">
                            Dev/Test: email will be sent only to CC (not to To/supplier), even if you edit the addresses below.
                        </div>
                    }
                    <div className="purchase-requisition-checkout-dialog__table-wrap">
                        <table className="purchase-requisition-checkout-dialog__table">
                            <thead>
                                <tr>
                                    <th>Supplier</th>
                                    <th className="purchase-requisition-checkout-dialog__option-col">Print</th>
                                    <th className="purchase-requisition-checkout-dialog__option-col">Email</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map(group => {
                                    const options = this.state.checkoutOptions[group.key] || {print: false, email: false, supplierEmail: '', ccEmail: ''};
                                    const itemCount = (group.items || []).length;
                                    return (
                                        <React.Fragment key={group.key}>
                                            <tr>
                                                <td>
                                                    <div className="purchase-requisition-checkout-dialog__supplier-name">
                                                        {group.supplierName}
                                                    </div>
                                                    <div className="purchase-requisition-checkout-dialog__item-count">
                                                        {itemCount} item{itemCount === 1 ? '' : 's'}
                                                    </div>
                                                </td>
                                                <td className="purchase-requisition-checkout-dialog__option-col">
                                                    <Checkbox
                                                        inputId={`checkout-print-${group.key}`}
                                                        checked={options.print === true}
                                                        onChange={(e) => this.toggleCheckoutOption(group.key, 'print', e.checked)}
                                                    />
                                                </td>
                                                <td className="purchase-requisition-checkout-dialog__option-col">
                                                    <Checkbox
                                                        inputId={`checkout-email-${group.key}`}
                                                        checked={options.email === true}
                                                        onChange={(e) => this.toggleCheckoutOption(group.key, 'email', e.checked)}
                                                    />
                                                </td>
                                            </tr>
                                            {options.email &&
                                                <tr className="purchase-requisition-checkout-dialog__email-row">
                                                    <td colSpan={3}>
                                                        <div className="purchase-requisition-checkout-dialog__email-fields">
                                                            <div className="purchase-requisition-checkout-dialog__email-field">
                                                                <Label htmlFor={`checkout-to-email-${group.key}`} className="purchase-requisition-checkout-dialog__email-label">
                                                                    To
                                                                </Label>
                                                                <AvField
                                                                    id={`checkout-to-email-${group.key}`}
                                                                    type="email"
                                                                    name={`toEmail-${group.key}`}
                                                                    value={options.supplierEmail || ''}
                                                                    onChange={(e) => this.updateCheckoutSupplierEmail(group.key, e.target.value)}
                                                                    placeholder="supplier@example.com"
                                                                />
                                                            </div>
                                                            <div className="purchase-requisition-checkout-dialog__email-field">
                                                                <Label htmlFor={`checkout-cc-email-${group.key}`} className="purchase-requisition-checkout-dialog__email-label">
                                                                    CC
                                                                </Label>
                                                                <AvField
                                                                    id={`checkout-cc-email-${group.key}`}
                                                                    type="email"
                                                                    name={`ccEmail-${group.key}`}
                                                                    value={options.ccEmail || ''}
                                                                    onChange={(e) => this.updateCheckoutCcEmail(group.key, e.target.value)}
                                                                    placeholder="your@email.com"
                                                                />
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            }
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <div className="purchase-requisition-checkout-dialog__footer">
                        <Button color="outline-primary" className="settings-page__btn--save" onClick={this.confirmCheckout}>
                            Confirm
                        </Button>
                        <Button color="outline-primary" className="settings-page__btn--cancel" onClick={this.closeCheckoutDialog}>
                            <Translate contentKey="entity.action.cancel">Cancel</Translate>
                        </Button>
                    </div>
                </div>
            </Dialog>
        );
    }

    renderSupplierGroups() {
        const groups = groupItemsBySupplier(this.state.requisition.itemList);
        if (groups.length === 0) {
            return (
                <div className="text-muted" style={{padding: '12px 0'}}>
                    No items added yet.
                </div>
            );
        }

        return groups.map(group => {
            const emailSent = wasSupplierEmailSent(this.state.requisition, group);
            const mailDetails = getSupplierMailDetails(group);
            return (
            <div key={group.key} style={{marginBottom: '24px'}}>
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                    <div className="d-flex flex-column gap-1">
                        <div className="d-flex align-items-center flex-wrap gap-2">
                            <h5 className="mb-0">{group.supplierName}</h5>
                            {emailSent &&
                                <span className="purchase-requisition-supplier-email-badge">
                                    <i className="pi pi-envelope" aria-hidden="true"/>
                                    Email sent{this.state.requisition.checkoutDate ? ` · ${this.state.requisition.checkoutDate}` : ''}
                                </span>
                            }
                        </div>
                        {mailDetails &&
                            <div className="purchase-requisition-supplier-email-addresses">
                                {!GeneralUtils.isNullOrEmpty(mailDetails.to) &&
                                    <span>To: {mailDetails.to}</span>
                                }
                                {!GeneralUtils.isNullOrEmpty(mailDetails.to) && !GeneralUtils.isNullOrEmpty(mailDetails.cc) &&
                                    <span className="purchase-requisition-supplier-email-addresses__sep" aria-hidden="true">·</span>
                                }
                                {!GeneralUtils.isNullOrEmpty(mailDetails.cc) &&
                                    <span>CC: {mailDetails.cc}</span>
                                }
                            </div>
                        }
                    </div>
                    <Button
                        color="outline-primary"
                        className="settings-page__btn--action"
                        onClick={() => exportSupplierPdf(group, this.state.requisition)}
                    >
                        <i className="pi pi-file-pdf" aria-hidden="true"/>
                        <Translate contentKey="entity.action.pdf">Print PDF</Translate>
                    </Button>
                </div>
                <hr className="mt-0 mb-3"/>
                <PurchaseRequisitionItemDataTable
                    data={group.items}
                    selectionMode="single"
                    paginator={true}
                    rows={20}
                    onDataSelect={this.onDataSelect}
                    delete={this.isEditable() ? this.showRemoveItemConfirm : undefined}
                    fields={this.getItemListFields()}
                />
            </div>
            );
        });
    }

    render() {
        if (GeneralUtils.isMobile()) {
            return null;
        }

        const editable = this.isEditable();
        const isDelivery = this.isDeliveryNote();

        return (
            <div className="settings-page settings-page--full col-md-12">
                <AvForm autocomplete="off" onSubmit={(e) => e.preventDefault()}>
                    <div className="settings-page__shell">
                        <ModalConfirm
                            header="Warning"
                            ok="OK"
                            onOkClick={this.deleteRequisition}
                            onCancelClick={() => this.setState({displayDeleteConfirmDialog: false})}
                            show={this.state.displayDeleteConfirmDialog}
                            message="Are you sure to delete?"
                        />
                        <ModalConfirm
                            header="Warning"
                            ok="OK"
                            onOkClick={this.confirmRemoveItem}
                            onCancelClick={() => this.setState({displayRemoveItemConfirmDialog: false, itemToRemove: null})}
                            show={this.state.displayRemoveItemConfirmDialog}
                            message="Are you sure to remove item?"
                        />
                        <ModalConfirm
                            header="Warning"
                            ok="OK"
                            onOkClick={this.executeCheckout}
                            onCancelClick={() => this.setState({displayCheckoutConfirmDialog: false})}
                            show={this.state.displayCheckoutConfirmDialog}
                            message="Are you sure to checkout? Status will be updated to Purchase Order."
                        />
                        <ModalConfirm
                            header="Warning"
                            ok="OK"
                            onOkClick={this.executeApprove}
                            onCancelClick={() => this.setState({displayApproveConfirmDialog: false})}
                            show={this.state.displayApproveConfirmDialog}
                            message="Are you sure to approve? Delivery Note will be locked (Closed)."
                        />

                        <div className="settings-page__header">
                            <h1 className="settings-page__title">
                                {isDelivery ? 'Delivery Note Detail' : 'Purchase Requisition Detail'}
                            </h1>
                        </div>

                        <div className="settings-page__panel purchase-requisition-save__header-panel">
                            <FormSection className="purchase-requisition-save__header-section">
                                <Row className="purchase-requisition-save__header-row">
                                    {isDelivery ? (
                                        <>
                                            <Col md="7" className="purchase-requisition-save__header-main">
                                                <Row className="purchase-requisition-save__header-fields">
                                                    <Col md="4">
                                                        <AvGroup>
                                                            <Label for="requisitionNo">Delivery Note No</Label>
                                                            <AvField
                                                                type="text"
                                                                name="requisitionNo"
                                                                disabled={true}
                                                                className="purchase-requisition-save__status-input"
                                                                value={this.state.requisition.requisitionNo || ''}
                                                            />
                                                        </AvGroup>
                                                    </Col>
                                                    <Col md="4">
                                                        <AvGroup>
                                                            <Label for="createDate">
                                                                <Translate contentKey="merchantBranch.updateDate">Create Date</Translate>
                                                            </Label>
                                                            <AvField
                                                                type="text"
                                                                name="createDate"
                                                                disabled={true}
                                                                className="purchase-requisition-save__create-date-input"
                                                                value={this.state.requisition.createDate || ''}
                                                            />
                                                        </AvGroup>
                                                    </Col>
                                                    <Col md="4">
                                                        <AvGroup>
                                                            <Label for="requisitionStatus">Status</Label>
                                                            <AvField
                                                                type="text"
                                                                name="requisitionStatus"
                                                                disabled={true}
                                                                className="purchase-requisition-save__status-input"
                                                                value={this.getRequisitionStatusLabel(this.state.requisition.requisitionStatus)}
                                                            />
                                                        </AvGroup>
                                                    </Col>
                                                </Row>
                                                <Row className="purchase-requisition-save__header-fields purchase-requisition-save__header-fields--description">
                                                    <Col md="12">
                                                        <AvGroup className="purchase-requisition-save__description-group">
                                                            <Label for="description">Description</Label>
                                                            <AvField
                                                                type="text"
                                                                name="description"
                                                                disabled={!editable}
                                                                value={this.state.requisition.description || ''}
                                                                onChange={this.__handleChange}
                                                            />
                                                        </AvGroup>
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col md="5" className="purchase-requisition-save__header-po">
                                                {this.renderAssignedPurchaseOrderCard()}
                                            </Col>
                                        </>
                                    ) : (
                                        <>
                                            <Col md="2" className="purchase-requisition-save__create-date-col">
                                                <AvGroup>
                                                    <Label for="requisitionNo">Requisition No</Label>
                                                    <AvField
                                                        type="text"
                                                        name="requisitionNo"
                                                        disabled={true}
                                                        className="purchase-requisition-save__status-input"
                                                        value={this.state.requisition.requisitionNo || ''}
                                                    />
                                                </AvGroup>
                                            </Col>
                                            <Col md="2">
                                                <AvGroup>
                                                    <Label for="createDate">
                                                        <Translate contentKey="merchantBranch.updateDate">Create Date</Translate>
                                                    </Label>
                                                    <AvField
                                                        type="text"
                                                        name="createDate"
                                                        disabled={true}
                                                        className="purchase-requisition-save__create-date-input"
                                                        value={this.state.requisition.createDate || ''}
                                                    />
                                                </AvGroup>
                                            </Col>
                                            <Col md="2">
                                                <AvGroup>
                                                    <Label for="requisitionStatus">Status</Label>
                                                    <AvField
                                                        type="text"
                                                        name="requisitionStatus"
                                                        disabled={true}
                                                        className="purchase-requisition-save__status-input"
                                                        value={this.getRequisitionStatusLabel(this.state.requisition.requisitionStatus)}
                                                    />
                                                </AvGroup>
                                            </Col>
                                            <Col md="2">
                                                <AvGroup>
                                                    <Label for="checkoutDate">Checkout Date</Label>
                                                    <AvField
                                                        type="text"
                                                        name="checkoutDate"
                                                        disabled={true}
                                                        className="purchase-requisition-save__status-input"
                                                        value={this.state.requisition.checkoutDate || ''}
                                                    />
                                                </AvGroup>
                                            </Col>
                                            <Col md="2">
                                                <AvGroup>
                                                    <Label for="description">Description</Label>
                                                    <AvField
                                                        type="text"
                                                        name="description"
                                                        disabled={!editable}
                                                        value={this.state.requisition.description || ''}
                                                        onChange={this.__handleChange}
                                                    />
                                                </AvGroup>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </FormSection>
                        </div>

                        <div className="settings-page__panel">
                            {editable &&
                                <div className="d-flex justify-content-end mb-3">
                                    <Button color="outline-primary" className="settings-page__btn--add" onClick={this.addNewItem}>
                                        <i className="pi pi-plus" aria-hidden="true"/>
                                        <Translate contentKey="entity.action.add">Add Item</Translate>
                                    </Button>
                                </div>
                            }
                            {this.renderItemList()}
                            <div
                                className="settings-page__actions settings-page__actions--dialog-footer settings-page__actions--equal-height"
                                style={{justifyContent: 'flex-start'}}
                            >
                                {editable && !isDelivery &&
                                    <Button
                                        color="outline-primary"
                                        className="settings-page__btn--save"
                                        onClick={this.openCheckoutDialog}
                                        disabled={!this.hasSavedRequisition()}
                                    >
                                        Checkout
                                    </Button>
                                }
                                {editable && isDelivery &&
                                    <Button
                                        color="outline-primary"
                                        className="settings-page__btn--save"
                                        onClick={this.openApproveDialog}
                                        disabled={!this.hasSavedRequisition()}
                                    >
                                        Approve
                                    </Button>
                                }
                                {editable &&
                                    <Button
                                        color="outline-primary"
                                        className="settings-page__btn--save"
                                        onClick={this.saveRequisition}
                                    >
                                        <Translate contentKey="entity.action.add">Save</Translate>
                                    </Button>
                                }
                                <Button
                                    color="outline-primary"
                                    className="settings-page__btn--cancel settings-page__btn--back"
                                    onClick={() => { window.location.href = '#purchaseRequisition'; }}
                                >
                                    <i className="pi pi-arrow-left" aria-hidden="true"/>
                                    <Translate contentKey="entity.action.cancel">Back</Translate>
                                </Button>
                                {this.hasSavedRequisition() && !this.isApproved() &&
                                    <Button
                                        color="outline-danger"
                                        className="settings-page__btn--delete"
                                        onClick={this.showDeleteConfirmDialog}
                                    >
                                        <Translate contentKey="entity.action.delete">Delete</Translate>
                                    </Button>
                                }
                            </div>
                        </div>
                    </div>

                    <Dialog
                        visible={this.state.displayDialog}
                        header={editable ? 'Product' : 'Product (View)'}
                        onHide={this.closeItemDialog}
                        style={GeneralUtils.getDialogStyleForPurchaseRequisitionItem()}
                    >
                        <PurchaseRequisitionItemMobileSave
                            webPopup={true}
                            readOnly={!editable}
                            deliveryMode={isDelivery}
                            showCostAlternatives={editable && !isDelivery}
                            ref={(elem) => this.refModelSave = elem}
                            saveItem={this.saveItem}
                            saveItemAndNext={this.saveItemAndNext}
                            closeItemDialog={this.closeItemDialog}
                        />
                    </Dialog>
                    {this.renderCheckoutDialog()}
                    {this.renderAssignPurchaseOrderDialog()}
                </AvForm>
                {this.state.loading && <Loading/>}
            </div>
        );
    }
}
